import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { db } from '../services/firebase'
import {
    collection,
    query,
    orderBy,
    limit,
    getDocs,
    startAfter,
    where,
    deleteDoc,
    doc,
    onSnapshot,
    getCountFromServer
} from 'firebase/firestore'
import './MarkDatabasePage.css'
import { useAuth } from '../context/AuthContext'

function MarkDatabasePage() {
    const { userProfile } = useAuth()
    const [marks, setMarks] = useState([])
    // Force refresh for automated re-indexing
    const [lastVisible, setLastVisible] = useState(null)
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [totalCount, setTotalCount] = useState(0)

    const PAGE_SIZE = 24
    const [reindexState, setReindexState] = useState(null)

    // Listen to background re-indexing progress
    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, 'config', 'reindexing'),
            (docSnap) => {
                if (docSnap.exists()) {
                    setReindexState(docSnap.data());
                }
            },
            (error) => {
                console.error("Re-indexing listener error:", error);
            }
        );
        return () => unsub();
    }, []);

    const toggleReindexing = async (event) => {
        const isRunning = reindexState?.status === 'running';

        if (isRunning) {
            if (!confirm('Stop the background re-indexing process?')) return;
            try {
                await fetch('https://europe-west1-porcelain-identification-78c76.cloudfunctions.net/stopReindexing', { method: 'POST' });
            } catch (e) {
                alert('Failed to stop: ' + e.message);
            }
        } else {
            // Shift+Click = full re-index (re-processes ALL marks, even those already indexed)
            const forceFullReindex = event?.shiftKey || false;

            let message;
            if (forceFullReindex) {
                message = 'FULL RE-INDEX: This will reprocess ALL marks from scratch, including those already indexed. This is time and resource intensive.\n\nAre you sure?';
            } else {
                message = 'Start incremental indexation? This will only process newly added marks that are missing embeddings. Already-indexed marks are skipped.\n\nTip: Hold Shift and click to force a FULL re-index of everything.';
            }

            if (!confirm(message)) return;

            try {
                // force=true always resets the state tracker for a clean run
                // smartScan=true (default) = skip already-indexed marks (incremental)
                // smartScan=false (Shift+click) = reprocess everything
                const smartScanParam = forceFullReindex ? 'false' : 'true';
                await fetch(`https://europe-west1-porcelain-identification-78c76.cloudfunctions.net/startReindexing?force=true&smartScan=${smartScanParam}&batchSize=5&concurrency=4&shards=4`, { method: 'POST' });
            } catch (e) {
                alert('Failed to start: ' + e.message);
            }
        }
    };

    const fetchMarks = useCallback(async (isNextPage = false) => {
        setLoading(true)
        try {
            let q;
            if (searchQuery.trim()) {
                const searchLower = searchQuery.toLowerCase();
                q = query(
                    collection(db, 'marks'),
                    where('manufacturer_lowercase', '>=', searchLower),
                    where('manufacturer_lowercase', '<=', searchLower + '\uf8ff'),
                    orderBy('manufacturer_lowercase'),
                    limit(PAGE_SIZE)
                );
            } else {
                q = query(
                    collection(db, 'marks'),
                    orderBy('createdAt', 'desc'),
                    limit(PAGE_SIZE)
                );
            }

            if (isNextPage && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const snapshot = await getDocs(q)
            const newMarks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

            if (isNextPage) {
                setMarks(prev => [...prev, ...newMarks])
            } else {
                setMarks(newMarks)
            }

            setLastVisible(snapshot.docs[snapshot.docs.length - 1])
            setHasMore(snapshot.docs.length === PAGE_SIZE)

            // Get total count once (Server-side efficient count)
            if (!isNextPage && !searchQuery) {
                const countSnap = await getCountFromServer(collection(db, 'marks'))
                setTotalCount(countSnap.data().count)
            }
        } catch (err) {
            console.error('Error fetching marks:', err)
        } finally {
            setLoading(false)
        }
    }, [searchQuery, lastVisible])

    useEffect(() => {
        // Reset and fetch when search changes
        const delayDebounceFn = setTimeout(() => {
            setLastVisible(null)
            fetchMarks(false)
        }, 500)

        return () => clearTimeout(delayDebounceFn)
    }, [searchQuery])

    const handleDeleteMark = async (id) => {
        if (userProfile?.role !== 'admin') return
        if (!confirm('Are you sure you want to delete this hallmark?')) return
        try {
            await deleteDoc(doc(db, 'marks', id))
            setMarks(prev => prev.filter(m => m.id !== id))
            setTotalCount(prev => prev - 1)
        } catch (err) {
            console.error('Error deleting mark:', err)
            alert('Failed to delete mark')
        }
    }

    const getProxyUrl = (url) => {
        if (!url) return 'https://placehold.co/200x200?text=No+Image';
        if (url.includes('firebasestorage.googleapis.com') || url.startsWith('blob:')) return url;
        const proxyBase = 'https://proxyimage-hlhgvkwroq-ew.a.run.app';
        return `${proxyBase}?url=${encodeURIComponent(url)}`;
    };

    return (
        <div className="mark-db-page">
            <div className="container">
                <div className="page-header">
                    <div className="header-content">
                        {userProfile?.role === 'admin' ? (
                            <Link to="/admin" className="back-link">← Back to Dashboard</Link>
                        ) : (
                            <Link to="/" className="back-link">← Back to Home</Link>
                        )}
                        <h1>Hallmark Database</h1>
                        <p>Browse and search through {totalCount} discovered marks</p>
                    </div>
                    {userProfile?.role === 'admin' && (
                        <button
                            className={`btn ${reindexState?.status === 'running' ? 'btn-danger' : 'btn-primary'}`}
                            onClick={toggleReindexing}
                            title={reindexState?.status === 'running'
                                ? 'Stop re-indexing'
                                : 'Index new marks (Shift+Click to force full re-index of all marks)'}
                        >
                            {reindexState?.status === 'running'
                                ? `⏹ Stop Re-indexing (${Math.round((reindexState.processed / (reindexState.total || 1)) * 100)}%)`
                                : '▶ Index New Marks'}
                        </button>
                    )}
                    {userProfile?.role === 'admin' && reindexState?.status === 'running' && (
                        <div className="progress-status" style={{ textAlign: 'right', fontSize: '0.8rem', marginTop: '5px' }}>
                            {reindexState.processed} / {reindexState.total} (Errors: {reindexState.errors})
                        </div>
                    )}
                </div>

                <div className="search-bar card-glass">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        placeholder="Search by manufacturer name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {loading && <div className="loader-small"></div>}
                </div>

                <div className="marks-grid">
                    {marks.map(mark => (
                        <div key={mark.id} className="mark-card card-glass">
                            <div className="mark-img">
                                <img src={getProxyUrl(mark.markImageUrl || mark.imageUrl)} alt={mark.manufacturer} loading="lazy" />
                            </div>
                            <div className="mark-info">
                                <h3>{mark.manufacturer}</h3>
                                <p className="mark-meta">{mark.country} {mark.period ? `(${mark.period})` : ''}</p>
                                {mark.description && <p className="mark-desc">{mark.description}</p>}
                                <div className="mark-footer">
                                    <span className="mark-source">Source: {mark.source || 'Unknown'}</span>
                                    {userProfile?.role === 'admin' && (
                                        <button onClick={() => handleDeleteMark(mark.id)} className="btn-delete" title="Delete Mark">🗑️</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {marks.length === 0 && !loading && (
                    <div className="empty-state">
                        <p>No marks found matching your criteria.</p>
                    </div>
                )}

                {hasMore && (
                    <div className="load-more">
                        <button
                            className="btn btn-secondary"
                            onClick={() => fetchMarks(true)}
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Load More Marks'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("MarkDatabasePage Error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '20px', color: 'red' }}>
                    <h2>Something went wrong.</h2>
                    <pre>{this.state.error?.toString()}</pre>
                </div>
            );
        }

        return this.props.children;
    }
}

export default function MarkDatabasePageWithBoundary() {
    return (
        <ErrorBoundary>
            <MarkDatabasePage />
        </ErrorBoundary>
    )
}
